import React, { useEffect, useMemo, useState } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useNavigate } from "react-router-dom";
import {
  useOffersListStore,
  selectHasPendingChanges,
  type OfferSummary,
} from "../../store/useOffersListStore";
import { useShopifySaveBar } from "../../hooks/useShopifySaveBar";
import { NavigationBlocker } from "../../components/common/NavigationBlocker";
import { PageBreadcrumbs } from "../../components/common/PageBreadcrumbs";
import { ListSearchBar } from "../../components/shared/ListSearchBar";
import { SalesBoosterTabBar } from "./SalesBoosterTabBar";
import { ListEmptySearchResults } from "../../components/shared/ListEmptySearchResults";

// ─── Sub-components ───────────────────────────────────────────────────────────

function QuantityOffersTable({
  offers,
  allOffersCount,
  hasPendingChanges,
  searchQuery,
  onSearchChange,
  onEdit,
  onAdd,
  onToggleActive,
  onDelete,
  onDuplicate,
}: {
  offers: OfferSummary[];
  allOffersCount: number;
  hasPendingChanges: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onEdit: (id: string) => void;
  onAdd: () => void;
  onToggleActive: (id: string, next: boolean) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
}) {
  // Empty state: no quantity offers exist on the server.
  if (allOffersCount === 0) {
    return (
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-400"
      >
        <s-stack direction="block" gap="base" alignItems="center">
          <s-icon type="price-list" />
          <s-heading>
            <strong>Crea tus ofertas de cantidad</strong>
          </s-heading>
          <s-text color="subdued">
            En esta sección puedes crear ofertas de cantidad para tu formulario
            COD.{" "}
            <strong>
              Las ofertas aparecerán directamente en el formulario.
            </strong>
          </s-text>
          <s-button variant="primary" icon="plus" onClick={onAdd}>
            Añadir oferta
          </s-button>
        </s-stack>
      </s-box>
    );
  }

  // Empty state: every offer is staged for deletion.
  if (offers.length === 0 && !searchQuery && hasPendingChanges) {
    return (
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-400"
      >
        <s-stack direction="block" gap="base" alignItems="center">
          <s-icon type="price-list" />
          <s-text color="subdued">
            Todas las ofertas están marcadas para eliminar.
          </s-text>
          <s-text color="subdued">
            Guarda para confirmar o descarta los cambios para restaurarlos.
          </s-text>
        </s-stack>
      </s-box>
    );
  }

  return (
    <>
      <ListSearchBar value={searchQuery} onChange={onSearchChange} />

      {offers.length === 0 ? (
        <ListEmptySearchResults onClear={() => onSearchChange("")}>
          Ninguna oferta coincide con tu búsqueda.
        </ListEmptySearchResults>
      ) : (
        <s-table>
          <s-table-header-row>
            <s-table-header>Activo</s-table-header>
            <s-table-header>Nombre</s-table-header>
            <s-table-header>Acciones</s-table-header>
          </s-table-header-row>
          <s-table-body>
            {offers.map((offer) => (
              <s-table-row key={offer.id}>
                <s-table-cell>
                  <s-switch
                    label="Activar oferta"
                    labelAccessibilityVisibility="exclusive"
                    checked={offer.is_active}
                    onChange={(e: CallbackEvent<"s-switch">) =>
                      onToggleActive(offer.id, e.currentTarget.checked)
                    }
                  />
                </s-table-cell>
                <s-table-cell>{offer.internal_name}</s-table-cell>
                <s-table-cell>
                  <s-stack direction="inline" gap="small-200">
                    <s-button
                      variant="tertiary"
                      icon="edit"
                      onClick={() => onEdit(offer.id)}
                    />
                    <s-button
                      variant="tertiary"
                      icon="duplicate"
                      onClick={() => onDuplicate(offer.id)}
                    />
                    <s-button
                      variant="tertiary"
                      icon="delete"
                      tone="critical"
                      onClick={() => onDelete(offer.id)}
                    />
                  </s-stack>
                </s-table-cell>
              </s-table-row>
            ))}
          </s-table-body>
        </s-table>
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const QuantityOffersPage: React.FC = () => {
  const navigate = useNavigate();

  // Gate by kind so we don't briefly render rows from another list
  // page on the first paint after navigation. See UpsellsOneTickPage
  // for the full explanation.
  const offers = useOffersListStore((s) =>
    s.query.kind === "quantity_tiers" ? s.offers : []
  );
  const pendingDeleteIds = useOffersListStore((s) => s.pendingDeleteIds);
  const isLoading = useOffersListStore((s) => s.isLoading);
  const isCommitting = useOffersListStore((s) => s.isCommitting);
  const error = useOffersListStore((s) => s.error);
  const isAwaitingOurKind = useOffersListStore(
    (s) => s.query.kind !== "quantity_tiers" || s.lastFetchedAt === null
  );
  const loadOffers = useOffersListStore((s) => s.loadOffers);
  const toggleActive = useOffersListStore((s) => s.toggleActive);
  const duplicateOffer = useOffersListStore((s) => s.duplicateOffer);
  const stageDelete = useOffersListStore((s) => s.stageDelete);
  const commitPending = useOffersListStore((s) => s.commitPending);
  const discardPending = useOffersListStore((s) => s.discardPending);

  const hasPendingChanges = useOffersListStore(selectHasPendingChanges);

  const visibleOffers = useMemo(() => {
    if (pendingDeleteIds.length === 0) return offers;
    const pending = new Set(pendingDeleteIds);
    return offers.filter((o) => !pending.has(o.id));
  }, [offers, pendingDeleteIds]);

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadOffers({ kind: "quantity_tiers" });
  }, [loadOffers]);

  const searchedOffers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visibleOffers;
    return visibleOffers.filter((o) =>
      o.internal_name.toLowerCase().includes(q)
    );
  }, [visibleOffers, searchQuery]);

  const handleEdit = (id: string) =>
    navigate(`/sales-booster/quantity-offers/${id}`);
  const handleAdd = () => navigate("/sales-booster/quantity-offers/new");

  const handleToggleActive = async (id: string, next: boolean) => {
    try {
      await toggleActive(id, next);
      window.shopify?.toast?.show(
        next ? "Oferta activada" : "Oferta desactivada"
      );
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "No se pudo cambiar el estado de la oferta";
      window.shopify?.toast?.show(message, { isError: true });
    }
  };

  const handleDelete = (id: string) => stageDelete(id);

  const handleDuplicate = async (id: string) => {
    try {
      await duplicateOffer(id);
      window.shopify?.toast?.show(
        "Oferta duplicada como borrador inactivo. Revísala y actívala cuando estés lista."
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "No se pudo duplicar la oferta";
      window.shopify?.toast?.show(message, { isError: true });
    }
  };

  useShopifySaveBar({
    isDirty: hasPendingChanges,
    isSaving: isCommitting,
    onSave: async () => {
      await commitPending();
    },
    onDiscard: discardPending,
  });

  const showInitialLoading = isLoading || isAwaitingOurKind;

  return (
    <div>
      <NavigationBlocker isDirty={hasPendingChanges} />

      <s-page heading="Ofertas de Cantidad">
        <PageBreadcrumbs
          items={[{ label: "Impulsor de ventas", to: "/sales-booster" }]}
        />
        <s-stack direction="block" gap="large-200">
          <SalesBoosterTabBar />

          {error && !showInitialLoading && (
            <s-banner tone="critical">
              <s-text>{error}</s-text>
            </s-banner>
          )}
          <s-stack
            direction="inline"
            alignItems="center"
            justifyContent="space-between"
          >
            <s-box paddingBlockEnd="small-200">
              <s-heading>
                <strong>Ofertas de Cantidad</strong>
              </s-heading>
            </s-box>
            <s-button variant="primary" icon="plus" onClick={handleAdd}>
              Añadir oferta
            </s-button>
          </s-stack>

          {showInitialLoading ? (
            <s-box
              background="base"
              border="small subdued solid"
              borderRadius="large-100"
              padding="large-400"
            >
              <s-stack direction="block" gap="base" alignItems="center">
                <s-text color="subdued">Cargando ofertas de cantidad…</s-text>
              </s-stack>
            </s-box>
          ) : (
            <s-section padding="none">
              <QuantityOffersTable
                offers={searchedOffers}
                allOffersCount={offers.length}
                hasPendingChanges={hasPendingChanges}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onEdit={handleEdit}
                onAdd={handleAdd}
                onToggleActive={handleToggleActive}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
              />
            </s-section>
          )}
        </s-stack>
      </s-page>
    </div>
  );
};

export default QuantityOffersPage;
